import { useState } from 'react';
import type { BotInfo } from '../api/types';
import { useBotAction, usePool, usePoolLogout, useReconnectPool } from '../queries';
import {
  Badge,
  Button,
  Card,
  Dialog,
  LoadingBlock,
  StateBlock,
  TableShell,
  Td,
  Th,
  toneForBotStatus,
} from '../ui';
import { useToast } from '../ui/toast';

function relativeAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

function formatLastUsed(value: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getUTCFullYear() <= 1) return 'never';
  return date.toLocaleString();
}

export default function PoolPage() {
  const toast = useToast();
  const { data, isPending, isError, error, refetch, isFetching } = usePool();
  const reconnect = useReconnectPool();
  const logoutAll = usePoolLogout();
  const botAction = useBotAction();

  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);

  const runBotAction = (bot: BotInfo, action: 'leave' | 'abandon' | 'logout') =>
    botAction.mutate(
      { botId: bot.bot_id, action },
      {
        onSuccess: () => toast.success(`${bot.username}: ${action} done`),
        onError: (mutationError) =>
          toast.error(`${bot.username}: ${action} failed`, (mutationError as Error).message),
      },
    );

  const anomalies = data?.anomalies ?? [];

  return (
    <div className="space-y-6">
      {anomalies.length > 0 && (
        <Card title="Accounts the pool cannot account for">
          <ul className="space-y-2">
            {anomalies.map((anomaly) => (
              <li
                key={anomaly.bot_id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-sm"
              >
                <Badge tone="warn">{anomaly.status}</Badge>
                <span className="font-medium text-ink">{anomaly.username}</span>
                <span className="text-ink-dim">{anomaly.reason}</span>
                <span className="ml-auto text-xs text-ink-faint">
                  {relativeAge(anomaly.age_seconds)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Bot pool"
        description={data ? `${data.total_accounts} account(s) configured` : undefined}
        actions={
          <>
            <Button size="sm" loading={isFetching} onClick={() => void refetch()}>
              Refresh
            </Button>
            <Button
              size="sm"
              loading={reconnect.isPending}
              onClick={() =>
                reconnect.mutate(false, {
                  onSuccess: (result) =>
                    toast.success(
                      `Reconnected ${result.reconnected}/${result.total_accounts}`,
                      result.failed > 0 ? `${result.failed} failed` : undefined,
                    ),
                  onError: (mutationError) =>
                    toast.error('Reconnect failed', (mutationError as Error).message),
                })
              }
            >
              Reconnect errored
            </Button>
            <Button
              size="sm"
              loading={reconnect.isPending}
              onClick={() =>
                reconnect.mutate(true, {
                  onSuccess: (result) =>
                    toast.success(`Force-reconnected ${result.reconnected}/${result.total_accounts}`),
                  onError: (mutationError) =>
                    toast.error('Reconnect failed', (mutationError as Error).message),
                })
              }
            >
              Force all
            </Button>
            <Button size="sm" variant="danger" onClick={() => setConfirmLogoutAll(true)}>
              Log out all
            </Button>
          </>
        }
      >
        {isPending ? (
          <LoadingBlock label="Loading pool…" />
        ) : isError ? (
          <StateBlock tone="bad" title="Could not load the pool" detail={(error as Error).message} />
        ) : (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              {Object.entries(data.status_counts ?? {}).map(([status, count]) => (
                <Badge key={status} tone={toneForBotStatus(status)}>
                  {status}: {count}
                </Badge>
              ))}
            </div>

            <TableShell>
              <thead>
                <tr>
                  <Th>Account</Th>
                  <Th>SteamID64</Th>
                  <Th>Status</Th>
                  <Th>Errors</Th>
                  <Th>Last used</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {data.accounts.map((bot) => (
                  <tr key={bot.bot_id || bot.username}>
                    <Td>
                      <div className="font-medium text-ink">{bot.profile_name || bot.username}</div>
                      <div className="font-mono text-[11px] text-ink-faint">{bot.bot_id || '—'}</div>
                    </Td>
                    <Td className="whitespace-nowrap font-mono text-xs text-ink-dim">
                      {bot.steam_id64 || 'Not connected yet'}
                    </Td>
                    <Td>
                      <Badge tone={toneForBotStatus(bot.status)}>{bot.status}</Badge>
                    </Td>
                    <Td className="text-xs text-ink-dim">{bot.error_count}</Td>
                    <Td className="text-xs text-ink-dim">{formatLastUsed(bot.last_used)}</Td>
                    <Td>
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          disabled={!bot.bot_id}
                          onClick={() => runBotAction(bot, 'leave')}
                        >
                          Leave
                        </Button>
                        <Button
                          size="sm"
                          disabled={!bot.bot_id}
                          onClick={() => runBotAction(bot, 'abandon')}
                        >
                          Abandon
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!bot.bot_id}
                          onClick={() => runBotAction(bot, 'logout')}
                        >
                          Log out
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          </>
        )}
      </Card>

      <Dialog
        open={confirmLogoutAll}
        onClose={() => setConfirmLogoutAll(false)}
        size="sm"
        title="Log out every bot?"
        description="Any bot currently hosting a lobby drops it."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmLogoutAll(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={logoutAll.isPending}
              onClick={() =>
                logoutAll.mutate(undefined, {
                  onSuccess: () => {
                    setConfirmLogoutAll(false);
                    toast.success('All bots logged out');
                  },
                  onError: (mutationError) =>
                    toast.error('Logout failed', (mutationError as Error).message),
                })
              }
            >
              Log out all
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-dim">Bots reconnect on demand for the next request.</p>
      </Dialog>
    </div>
  );
}
